import {
  InputGroup,
  InputRightElement,
  InputLeftElement,
  InputProps as ChakraInputProps,
  Input as ChakraInput,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FieldError } from "react-hook-form";
import { forwardRef, useEffect, useState } from "react";

import { resolveChakraSpacing } from "./helpers";
import { TranslationKeys } from "utils/localization";

type InputProps = ChakraInputProps & {
  error?: FieldError;
  required?: boolean;
  dataTest?: string;
  customError?: TranslationKeys;
  min?: number;
  max?: number;
  extValue?: number | null;
  isZeroAllowed?: boolean;
  setValueOutside?: (value: string | undefined) => void;
};

export const NumericInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error,
      required,
      dataTest,
      customError,
      min = 0,
      max = 30,
      extValue,
      isZeroAllowed,
      setValueOutside,
      ...props
    },
    ref,
  ) => {
    const newProps = resolveChakraSpacing<InputProps, InputProps>(props);

    const [value, setValue] = useState(extValue ?? "");

    const minusCondition = () => {
      if (isZeroAllowed) {
        return !value || (value && value > min);
      }

      return (min !== 0 || value) && (!value || (value && value > min));
    };

    const plusCondition = !value || (value && value < max);

    useEffect(() => {
      setValueOutside && setValueOutside(value?.toString());
    }, [value]);

    const minusHandler = () => {
      if (minusCondition()) {
        setValue((original) => {
          if (typeof original === "number" && original === 1) {
            return isZeroAllowed ? 0 : -1;
          } else if (typeof original === "number") {
            return --original;
          }

          return -1;
        });
      }
      document.getElementById(newProps.rest.name ?? "")?.focus();
    };

    const plusHandler = () => {
      if (plusCondition) {
        setValue((original) => {
          if (typeof original === "number" && original === -1) {
            return isZeroAllowed ? 0 : 1;
          } else if (typeof original === "number") {
            return ++original;
          }

          return 1;
        });
      }
      document.getElementById(newProps.rest.name || "")?.focus();
    };

    return (
      <>
        <InputGroup {...newProps.spacing}>
          <InputLeftElement onClick={minusHandler} cursor="pointer">
            <Flex
              bg={minusCondition() ? "ud-primary" : "ud-grey-300"}
              borderRadius="4px"
              p="0px 6px 2px"
              lineHeight="15px"
              textColor="ud-white"
              fontWeight="bold"
            >
              -
            </Flex>
          </InputLeftElement>
          <ChakraInput
            {...newProps.rest}
            type="number"
            _focusVisible={{ borderColor: "ud-grey-500" }}
            ref={ref}
            value={value}
            id={newProps.rest.name}
            textAlign="center"
            onChange={(e) => {
              const parsed = parseInt(e.currentTarget.value);
              setValue(parsed);
            }}
          />
          <InputRightElement onClick={plusHandler} cursor="pointer">
            <Flex
              bg={plusCondition ? "ud-primary" : "ud-grey-300"}
              borderRadius="4px"
              p="0px 4px 2px"
              lineHeight="15px"
              textColor="ud-white"
              fontWeight="bold"
            >
              +
            </Flex>
          </InputRightElement>
        </InputGroup>
        {error?.message && <Text variant="form-error">{error.message}</Text>}
      </>
    );
  },
);
